import React, { useState } from 'react';
import { useTenant } from '../../contexts/TenantContext';
import { useAuth } from '../../contexts/AuthContext';
import { TenantOnboardingRequest } from '../../api/identity';
import { promoCodeApi, referralApi, PromoCodeValidationResult, SignupBonusConfigDTO, billingApi } from '../../api/billing';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, ArrowRight, ArrowLeft, CheckCircle2, AlertCircle, Tag, Gift, Users } from 'lucide-react';
import { COMPANY_SIZE_OPTIONS, INDUSTRY_OPTIONS, USE_CASE_OPTIONS } from '@/types/tenant';
import { cn } from '@/lib/utils';
import { StepPips } from '@/components/shared/StepPips';


interface OnboardingWizardProps {
  onComplete?: () => void;
}

// Step icon wrapper
const StepIcon: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full border border-primary/20 bg-primary/10">
    {children}
  </div>
);

export const OnboardingWizard: React.FC<OnboardingWizardProps> = ({ onComplete }) => {
  const { onboard } = useTenant();
  const { user, logout } = useAuth();

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [orgNameError, setOrgNameError] = useState(false);
  const [success, setSuccess] = useState(false);
  const [signupBonusConfig, setSignupBonusConfig] = useState<SignupBonusConfigDTO | null>(null);

  // Form state
  const [organizationName, setOrganizationName] = useState('');
  const [description, setDescription] = useState('');
  const [industry, setIndustry] = useState('');
  const [companySize, setCompanySize] = useState('');
  const [primaryUseCase, setPrimaryUseCase] = useState('');
  const [promoCode, setPromoCode] = useState('');
  const [promoValidating, setPromoValidating] = useState(false);
  const [promoResult, setPromoResult] = useState<PromoCodeValidationResult | null>(null);
  const [promoApplied, setPromoApplied] = useState(false);
  const [grantedCredits, setGrantedCredits] = useState<number | null>(null);

  const [referralCode] = useState<string | null>(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const refFromUrl = urlParams.get('ref');
    if (refFromUrl) {
      localStorage.setItem('enveu_ref', refFromUrl);
      return refFromUrl;
    }
    return localStorage.getItem('enveu_ref');
  });
  const [referralApplied, setReferralApplied] = useState(false);

  React.useEffect(() => {
    billingApi.getSignupBonusConfig()
      .then(setSignupBonusConfig)
      .catch(() => { /* non-fatal — success screen falls back gracefully */ });
  }, []);

  const goStep = (n: number, dir: 'fwd' | 'back') => {
    if (dir === 'fwd' && step === 1) {
      if (organizationName.trim().length < 2) {
        setOrgNameError(true);
        return;
      }
    }
    setStep(n);
  };

  // Step 1 "Skip & Launch": skip the optional details step and create the
  // workspace directly. Preserves the default-org-name fallback when no name
  // is entered, then submits (instead of advancing to step 2).
  const handleSkipAndLaunch = () => {
    const name = organizationName.trim()
      ? organizationName
      : `Default Org - ${user?.email || user?.firstName || 'user'}`;
    setOrganizationName(name);
    setOrgNameError(false);
    handleSubmit(name);
  };

  const handleApplyPromo = async () => {
    const code = promoCode.trim();
    if (!code) return;
    setPromoValidating(true);
    setPromoResult(null);
    try {
      const result = await promoCodeApi.validate(code);
      setPromoResult(result);
      if (result.valid) setPromoApplied(true);
    } catch {
      setPromoResult({ valid: false, message: 'Could not validate. Please try again.', credits: null, code: promoCode.trim(), meteringKey: null, validityDays: null });
    } finally {
      setPromoValidating(false);
    }
  };

  const handleSubmit = async (orgNameOverride?: string) => {
    // Allow callers (e.g. Step 1 "Skip & Launch") to pass the name directly,
    // since a preceding setOrganizationName() has not flushed to state yet.
    const orgName = (orgNameOverride ?? organizationName).trim();
    if (!orgName) {
      setError('Organization name is required');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const request: TenantOnboardingRequest = {
        organizationName: orgName,
        description: description.trim() || undefined,
        industry: industry || undefined,
        companySize: companySize || undefined,
        primaryUseCase: primaryUseCase || undefined,
      };
      const response = await onboard(request);
      const tenantId = response?.tenant?.tenantId;

      if (promoApplied && promoCode.trim() && promoResult?.valid && tenantId) {
        try {
          await promoCodeApi.redeem(promoCode.trim(), tenantId);
          setGrantedCredits(promoResult.credits);
        } catch {
          // Non-fatal
        }
      }

      if (referralCode && tenantId) {
        try {
          await referralApi.processReferral(tenantId, referralCode);
          setReferralApplied(true);
          localStorage.removeItem('enveu_ref');
        } catch {
          // Non-fatal
        }
      }

      setSuccess(true);
      setTimeout(() => onComplete?.(), 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create organization');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-8 text-center">
            <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full border border-emerald-500/25 bg-emerald-500/10">
              <CheckCircle2 className="h-7 w-7 text-emerald-500" />
            </div>
            <CardTitle className="mb-2 text-2xl">Welcome to Enveu!</CardTitle>
            <CardDescription className="mb-5 text-sm leading-relaxed">
              Your organisation has been created successfully.
            </CardDescription>
            <div className="mb-4 space-y-1.5 rounded-lg border border-emerald-500/20 bg-emerald-500/5 px-4 py-3 text-left">
              {signupBonusConfig?.enabled !== false && (
                <p className="flex items-center gap-2 text-sm font-semibold text-emerald-600">
                  <Gift className="h-4 w-4 shrink-0" />
                  You've received{' '}
                  {signupBonusConfig
                    ? signupBonusConfig.credits.toLocaleString()
                    : '—'}{' '}
                  free credits to get started!
                </p>
              )}
              {grantedCredits != null && (
                <p className="flex items-center gap-2 text-sm font-semibold text-emerald-600">
                  <Tag className="h-4 w-4 shrink-0" />
                  Promo applied — {grantedCredits.toLocaleString()} bonus credits added!
                </p>
              )}
              {referralApplied && (
                <p className="flex items-center gap-2 text-sm font-semibold text-emerald-600">
                  <Users className="h-4 w-4 shrink-0" />
                  Referral bonus applied — extra credits added!
                </p>
              )}
            </div>
            <p className="text-xs text-muted-foreground">Redirecting to dashboard…</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background p-4 md:p-10">

      {/* Logo */}
      <div className="mb-8 flex items-center gap-2.5">
        <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-primary text-sm font-bold text-primary-foreground shadow-[0_0_18px_hsl(var(--primary)/0.3)]">
          E
        </div>
        <div>
          <div className="text-sm font-semibold leading-none text-foreground">Enveu</div>
          <div className="mt-0.5 text-[8.5px] font-bold uppercase tracking-[0.18em] text-primary">
            Flow Engine
          </div>
        </div>
      </div>

      {/* Card */}
      <Card className="w-full max-w-[520px]">
        {/* Accent bar */}
        <div className="h-0.5 rounded-t-xl bg-gradient-to-r from-transparent via-primary to-transparent" />

        <CardContent className="px-8 py-9 md:px-10">
          <StepPips current={step} total={2} className="mb-6" />

          {/* Global error */}
          {error && (
            <Alert variant="destructive" className="mb-5">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* ── STEP 1 — Create Organisation ── */}
          {step === 1 && (
            <div>
              <p className="mb-5 text-center text-[9.5px] font-bold uppercase tracking-widest text-muted-foreground">
                Step 1 of 3
              </p>

              <StepIcon>
                <svg className="h-6 w-6 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18M9 21V9" />
                </svg>
              </StepIcon>

              <CardTitle className="mb-2 text-center text-[22px] font-normal">
                Create Your Organisation
              </CardTitle>
              <CardDescription className="mx-auto mb-7 max-w-[380px] text-center text-[12.5px] leading-relaxed">
                Welcome, <strong className="text-foreground">{user?.firstName || 'there'}</strong>! Let's set up your Flow workspace.
              </CardDescription>

              <div className="mb-4 space-y-2">
                <Label htmlFor="org-name">
                  Organisation Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="org-name"
                  placeholder="e.g. Star Sports OTT"
                  autoFocus
                  autoComplete="organization"
                  value={organizationName}
                  onChange={(e) => { setOrganizationName(e.target.value); setOrgNameError(false); }}
                  className={cn(orgNameError && 'border-destructive focus-visible:ring-destructive/30')}
                />
                {orgNameError ? (
                  <p className="text-xs text-destructive">Please enter your organisation name.</p>
                ) : (
                  <p className="text-xs text-muted-foreground">This is your company or team name.</p>
                )}
              </div>

              <div className="mb-6 space-y-2">
                <Label htmlFor="org-desc" className="inline-flex items-center gap-1.5">
                  Description
                  <span className="rounded border border-border bg-muted px-1.5 py-0.5 text-[8.5px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Optional
                  </span>
                </Label>
                <Textarea
                  id="org-desc"
                  placeholder="Brief description of your organisation…"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                />
              </div>

              <div className="flex items-center gap-2.5">
                <Button className="flex-1" onClick={() => goStep(2, 'fwd')} disabled={loading}>
                  Continue <ArrowRight className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={handleSkipAndLaunch} disabled={loading} className="text-muted-foreground">
                  {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Launching…</> : 'Skip & Launch'}
                </Button>
              </div>
              <div className="mt-3 text-center">
                <Button variant="ghost" size="sm" onClick={logout} className="text-destructive/70 hover:text-destructive hover:bg-destructive/10">
                  Sign out
                </Button>
              </div>
            </div>
          )}

          {/* ── STEP 2 — About Your Org ── */}
          {step === 2 && (
            <div>
              <p className="mb-5 text-center text-[9.5px] font-bold uppercase tracking-widest text-muted-foreground">
                Step 2 of 2
              </p>

              <StepIcon>
                <svg className="h-6 w-6 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              </StepIcon>

              <CardTitle className="mb-2 text-center text-[22px] font-normal">
                Tell Us About You
              </CardTitle>
              <CardDescription className="mx-auto mb-7 max-w-[380px] text-center text-[12.5px] leading-relaxed">
                Helps us personalise your experience. All fields are optional.
              </CardDescription>

              <div className="mb-4 space-y-2">
                <Label htmlFor="industry" className="inline-flex items-center gap-1.5">
                  Industry
                  <span className="rounded border border-border bg-muted px-1.5 py-0.5 text-[8.5px] font-semibold uppercase tracking-wide text-muted-foreground">Optional</span>
                </Label>
                <Select value={industry || '__none__'} onValueChange={(v) => setIndustry(v === '__none__' ? '' : v)}>
                  <SelectTrigger id="industry">
                    <SelectValue placeholder="Select your industry" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__"><span className="text-muted-foreground">— None —</span></SelectItem>
                    {INDUSTRY_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="mb-4 space-y-2">
                <Label htmlFor="company-size" className="inline-flex items-center gap-1.5">
                  Company Size
                  <span className="rounded border border-border bg-muted px-1.5 py-0.5 text-[8.5px] font-semibold uppercase tracking-wide text-muted-foreground">Optional</span>
                </Label>
                <Select value={companySize || '__none__'} onValueChange={(v) => setCompanySize(v === '__none__' ? '' : v)}>
                  <SelectTrigger id="company-size">
                    <SelectValue placeholder="Select company size" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__"><span className="text-muted-foreground">— None —</span></SelectItem>
                    {COMPANY_SIZE_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="mb-6 space-y-2">
                <Label htmlFor="use-case" className="inline-flex items-center gap-1.5">
                  Primary Use Case
                  <span className="rounded border border-border bg-muted px-1.5 py-0.5 text-[8.5px] font-semibold uppercase tracking-wide text-muted-foreground">Optional</span>
                </Label>
                <Select value={primaryUseCase || '__none__'} onValueChange={(v) => setPrimaryUseCase(v === '__none__' ? '' : v)}>
                  <SelectTrigger id="use-case">
                    <SelectValue placeholder="What will you use Enveu Flow for?" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__"><span className="text-muted-foreground">— None —</span></SelectItem>
                    {USE_CASE_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-2.5">
                <Button variant="outline" onClick={() => goStep(1, 'back')}>
                  <ArrowLeft className="h-4 w-4" /> Back
                </Button>
                <Button className="flex-1" onClick={() => handleSubmit()} disabled={loading}>
                  {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Launching…</> : <>Launch My Workspace <ArrowRight className="h-4 w-4" /></>}
                </Button>
              </div>
            </div>
          )}

          {/* ── STEP 3 — Promo Code (hidden for MVP) ──
          {step === 3 && (
            <div>
              <p className="mb-5 text-center text-[9.5px] font-bold uppercase tracking-widest text-muted-foreground">
                Step 3 of 3
              </p>

              <StepIcon>
                <Tag className="h-6 w-6 text-primary" />
              </StepIcon>

              <CardTitle className="mb-2 text-center text-[22px] font-normal">
                Got a Promo Code?
              </CardTitle>
              <CardDescription className="mx-auto mb-7 max-w-[380px] text-center text-[12.5px] leading-relaxed">
                Enter a code to unlock a discount or extended trial. Completely optional — skip if you don't have one.
              </CardDescription>

              <div className="mb-3 space-y-2">
                <Label htmlFor="promo-code" className="inline-flex items-center gap-1.5">
                  Promo Code
                  <span className="rounded border border-border bg-muted px-1.5 py-0.5 text-[8.5px] font-semibold uppercase tracking-wide text-muted-foreground">Optional</span>
                </Label>
                <Input
                  id="promo-code"
                  placeholder="e.g. LAUNCH50"
                  value={promoCode}
                  maxLength={20}
                  autoComplete="off"
                  spellCheck={false}
                  onChange={(e) => {
                    setPromoCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''));
                    setPromoResult(null);
                    setPromoApplied(false);
                  }}
                  className={cn(
                    'font-mono tracking-widest',
                    promoResult?.valid && 'border-emerald-500 focus-visible:ring-emerald-500/30',
                    promoResult?.valid === false && 'border-destructive focus-visible:ring-destructive/30',
                  )}
                />
              </div>

              <div className="mb-3 flex items-center gap-2.5">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleApplyPromo}
                  disabled={promoValidating || !promoCode.trim()}
                  className="border-primary text-primary hover:bg-primary hover:text-primary-foreground disabled:border-muted-foreground/40 disabled:text-muted-foreground disabled:opacity-100"
                >
                  {promoValidating && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                  {promoValidating ? 'Checking…' : 'Apply Code'}
                </Button>
                {!promoResult && (
                  <span className="text-xs text-muted-foreground">
                    {promoCode.length > 0 ? 'Click Apply to validate your code' : 'Enter a code above to apply'}
                  </span>
                )}
              </div>

              {promoResult && (
                <Alert
                  className={cn(
                    'mb-3',
                    promoResult.valid && 'border-emerald-500/30 bg-emerald-500/5 text-emerald-600 [&>svg]:text-emerald-600',
                  )}
                  variant={promoResult.valid ? 'default' : 'destructive'}
                >
                  {promoResult.valid
                    ? <CheckCircle2 className="h-4 w-4" />
                    : <AlertCircle className="h-4 w-4" />
                  }
                  <AlertDescription>{promoResult.message}</AlertDescription>
                </Alert>
              )}

              <Separator className="my-6" />

              <Button variant="outline" onClick={() => goStep(2, 'back')} className="mb-3">
                <ArrowLeft className="h-4 w-4" /> Back
              </Button>

              <Button className="w-full" size="lg" onClick={handleSubmit} disabled={loading}>
                {loading ? (
                  <><Loader2 className="h-4 w-4 animate-spin" /> Launching…</>
                ) : (
                  <>Launch My Workspace <ArrowRight className="h-4 w-4" /></>
                )}
              </Button>

              <div className="mt-3 text-center">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleSubmit}
                  disabled={loading}
                  className="text-muted-foreground"
                >
                  Skip promo code and launch →
                </Button>
              </div>
            </div>
          )}
          */}
        </CardContent>
      </Card>
    </div>
  );
};

export default OnboardingWizard;
